import React, { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  ButtonIcon,
  SvgCloseCircleFilled,
  Input,
  SvgAdd,
  SvgMinus,
  NdsThemeProvider,
} from '@ntuctech/devex-tangram';
import styled from 'styled-components';

import AddIcon from '../../icons/svg/AddCircle.svg';
import { SCREEN_SIZE } from '../../../lib/mediaQuery';

// eslint-disable-next-line import/no-cycle
import { PlusMinusInput } from './AddReceipnt';

export const StyledIconButton = styled(ButtonIcon)`
  display: inline-block;
  margin: 0;
  width: 2rem;
  height: 2rem;
  svg {
    top: 0.25rem;
  }
  svg path {
    fill: ${(p) => (p.disabled ? '#cccccc' : '#fff')} !important;
  }
  :hover {
    background: #104393 !important;
  }
  :focus {
    background-color: #0b2e65;
    ::before {
      box-shadow: none;
    }
  }
  &:active {
    background-color: #0b2e65;
    ::before {
      box-shadow: none;
    }
  }
`;

export const Container = styled.div`
  padding: 2rem;
  background-color: #fff;
  border-radius: 0.5rem;
  border: 1px solid #eaeaea;
  margin-bottom: 1rem;
  .list-style {
    list-style: disc;
  }
  .marign-left-16 {
    margin-left: 1rem;
  }
  .margin-y-24 {
    margin: 1.5rem 0;
  }

  .no-margin-bottom {
    margin-bottom: 0 !important;
  }

  .mine-shaft-66 {
    color: #666666;
  }

  .input-full-width {
    width: 30rem;
    ${SCREEN_SIZE.Only.Tablet} {
      width: 21.688rem;
    }
    ${SCREEN_SIZE.Below.MobileL} {
      width: 20rem;
    }
  }
  .input-field {
    margin: 0 0.75rem !important;
    width: 3.5rem !important;
    border-radius: 0.25rem !important;
    border: 0.063rem !important;
    padding: 0.25rem 0.5rem !important;
    color: #1454b8 !important;
    top: -0.5rem;
  }
  ${SCREEN_SIZE.Below.MobileL} {
    padding: 1rem;
    border-radius: 0;
    border: 0;
  }
  ${SCREEN_SIZE.Only.Tablet} {
    padding: 1.5rem !important;
  }
  ${SCREEN_SIZE.From.Desktop} {
    padding: 2rem !important;
  }
`;

const BodyContainer = styled(Grid)`
  padding: 1.5rem 0 0;
  & path {
    fill: #666666 !important;
  }
  .cancel-button {
    padding: 0 !important;
    margin: 0 !important;
    bottom: 0.5rem;
    float: right;
    :hover {
      background: #cccccc !important;
    }
    :focus {
      ::before {
        box-shadow: 0rem 0rem 0rem 0.1875rem #cccccc !important;
      }
    }
    &:active {
      background-color: #cccccc;
      ::before {
        box-shadow: 0rem 0rem 0rem 0.1875rem #cccccc !important;
      }
    }
  }

  .flex {
    display: flex;
  }

  .pointer {
    cursor: pointer;
  }

  .absolute {
    position: absolute;
    & input {
      bottom: 0.125rem !important;
    }
  }

  & input {
    width: 5rem;
    height: 2rem;
    margin-left: 0.25rem;
    position: relative !important;
    bottom: 0.313rem !important;
  }

  .img-size {
    & img {
      width: 1.5rem;
      height: 1.5rem;
    }
  }

  .no-space {
    padding: 0 !important;
    margin: 0 !important;
  }
`;

const StyledGrid = styled(Grid)`
  padding: 0 !important;
  margin: 0 0 1.5rem !important;
  input[type='number']::-webkit-outer-spin-button,
  input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const StyledGridContainer = styled(Grid)`
  padding: 0 !important;
  margin: 0 !important;
`;

const StyledButtonCancel = styled(ButtonIcon)`
  & path {
    fill: ${(p) => p.disabled && 'rgba(51,51,51,0.35) !important'};
  }
`;

export const StyledTypography = styled(Typography)`
  font-size: 1rem;
  font-weight: ${(p) => (p.noMargin ? 400 : 700)};
  line-height: 1.5rem;
  letter-spacing: 0;
  color: #1454b8;
  margin-left: ${(p) => (p.noMargin ? 0 : '0.5rem')};
`;

const quantity = [
  {
    value: 20,
    qty: 0,
    amount: 0,
    isFixed: true,
  },
  {
    value: 50,
    qty: 0,
    amount: 0,
    isFixed: true,
  },
  {
    value: 100,
    qty: 0,
    amount: 0,
    isFixed: true,
  },
  {
    value: 0,
    qty: 0,
    amount: 0,
  },
];

export const mergeArrays = (arr1, arr2) => {
  // Create a map from arr1 for quick lookup
  const map1 = new Map(arr1.map((item) => [Number(item.amount), item.quantity]));

  // Merge arr1 into arr2
  const merged = arr2.map((item) => {
    if (map1.has(item.value)) {
      return { ...item, qty: map1.get(item.value) };
    }
    return item;
  });

  // Add items from arr1 that are not in arr2
  const remaining = arr1.filter((item) => !arr2.some((el) => el.value === Number(item.amount)));
  const remainingFormatted = remaining.map((item) => ({
    value: Number(item.amount),
    qty: item.quantity,
    amount: 0,
    isFixed: true,
  }));

  // Combine merged array and remainingFormatted array
  const combined = [...merged, ...remainingFormatted];

  // Move the item with value: 0 to the end
  const zeroValueItem = combined.find((item) => item.value === 0);
  const result = combined.filter((item) => item.value !== 0);
  if (zeroValueItem) {
    result.push(zeroValueItem);
  }

  return result;
};

const BussinessQuantity = ({ setVoucherData, voucherData = { denominations: [] }, isEditMode }) => {
  const [groupAmount, setGroupAmount] = useState(quantity);

  useEffect(() => {
    if (voucherData.denominations.length > 0 && isEditMode) {
      const voucherDataDenominations = voucherData.denominations;

      const result = mergeArrays(voucherDataDenominations, groupAmount);
      setGroupAmount(result);
    }
    return () => setGroupAmount(quantity);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucherData.denominations]);

  useEffect(() => {
    setVoucherData((prevState) => ({
      ...prevState,
      bussinessData: groupAmount,
    }));
  }, [groupAmount, setVoucherData]);

  return (
    <NdsThemeProvider>
      <Container container>
        <Typography variant="titleS">Select amount and quantity</Typography>
        <div>
          <Typography variant="bodyM">$1 minimum, $1,000 maximum.</Typography>
        </div>
        <Typography variant="bodyM">Vouchers can be used for multiple transactions. </Typography>
        <BodyContainer>
          <StyledGridContainer container>
            <StyledGrid item xs={4}>
              <Typography variant="bodySBold">Amount</Typography>
            </StyledGrid>
            <StyledGrid item xs={4}>
              <Typography variant="bodySBold">
                Quantity <Typography variant="bodyS">(max. 400,000)</Typography>
              </Typography>
            </StyledGrid>
            <StyledGrid item xs={4} />
            {groupAmount.map((qty, ind) => {
              const textValue = qty.value !== 0 && qty.value;
              const isDisabled = groupAmount[ind]?.qty === 0;
              return (
                <>
                  <StyledGrid item xs={4} className="flex">
                    <Typography variant="titleS">
                      $ {textValue}{' '}
                      {!qty.isFixed && (
                        <Input
                          data-testid="customise-input"
                          onChange={(e) => {
                            e.target.value.replace(/[^0-9]/g, '');
                            setGroupAmount((prevState) => {
                              const obj = prevState[ind];
                              obj.amount = Number(e.target?.value);
                              return [...prevState];
                            });
                          }}
                          color="#cccccc"
                          required
                          className="input-class absolute"
                          type="number"
                          min={0}
                          onKeyDown={(evt) =>
                            ['e', 'E', '+', '-', '.'].includes(evt.key) && evt.preventDefault()
                          }
                          onKeyUp={(e) => {
                            if (e.target?.value > 1000) {
                              e.target.value = 1000;
                              setGroupAmount((prevState) => {
                                const obj = prevState[ind];
                                obj.amount = Number(e.target?.value);
                                return [...prevState];
                              });
                            }
                          }}
                          placeholder="0"
                          label=""
                          value={
                            groupAmount[ind]?.amount > 0 &&
                            Math.round(Number(groupAmount[ind]?.amount))
                          }
                          name="modified-amount"
                        />
                      )}
                    </Typography>
                  </StyledGrid>
                  <StyledGrid item xs={6}>
                    <PlusMinusInput isBusiness isDisabled={isDisabled}>
                      <StyledIconButton
                        disabled={isDisabled}
                        data-testid="minus-btn"
                        onClick={() => {
                          setGroupAmount((prevState) => {
                            const obj = { ...prevState[ind] }; // Create a copy of the object
                            obj.qty -= 1;
                            return [...prevState.slice(0, ind), obj, ...prevState.slice(ind + 1)]; // Create a new array
                          });
                        }}
                      >
                        <SvgMinus />
                      </StyledIconButton>
                      <input
                        className="input-field"
                        type="number"
                        required
                        id="number"
                        data-testid="qty-modify"
                        placeholder="0"
                        min={0}
                        onKeyDown={(evt) =>
                          ['e', 'E', '+', '-', '.'].includes(evt.key) && evt.preventDefault()
                        }
                        value={
                          (groupAmount[ind]?.qty > 0 && groupAmount[ind]?.qty) ||
                          (groupAmount[ind]?.quantity > 0 && groupAmount[ind]?.quantity)
                        }
                        onChange={(e) => {
                          setGroupAmount((prevState) => {
                            const obj = prevState[ind];
                            obj.qty = Number(e.target?.value);
                            return [...prevState];
                          });
                        }}
                        name="count"
                      />
                      <StyledIconButton
                        data-testid="add-btn"
                        onClick={() => {
                          setGroupAmount((prevState) => {
                            const obj = { ...prevState[ind] };
                            obj.qty += 1;
                            return [...prevState.slice(0, ind), obj, ...prevState.slice(ind + 1)];
                          });
                        }}
                      >
                        <SvgAdd />
                      </StyledIconButton>
                    </PlusMinusInput>
                  </StyledGrid>
                  <StyledGrid item xs={2}>
                    <StyledButtonCancel
                      disabled={groupAmount.length === 1}
                      data-testid={`remove-icon-${ind}`}
                      onClick={() => {
                        setGroupAmount((prevState) => {
                          const obj = prevState;
                          obj.splice(ind, 1);
                          return [...prevState];
                        });
                      }}
                      className="cancel-button"
                      size="s"
                    >
                      <SvgCloseCircleFilled />
                    </StyledButtonCancel>
                  </StyledGrid>
                </>
              );
            })}
            <StyledGrid item xs={12} className="pointer">
              <Grid
                className="no-space flex img-size"
                data-testid="add-customise-amount"
                onClick={() => {
                  if (groupAmount.length < 10) {
                    setGroupAmount((prevState) => [
                      ...prevState,
                      {
                        text: '$',
                        value: 0,
                        qty: 0,
                        amount: 0,
                      },
                    ]);
                  }
                }}
              >
                <img src={AddIcon.src} alt={AddIcon} />
                <StyledTypography>Customise amount</StyledTypography>
              </Grid>
            </StyledGrid>
          </StyledGridContainer>
        </BodyContainer>
      </Container>
    </NdsThemeProvider>
  );
};

export default BussinessQuantity;
